import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, CircleUserRound, Menu, Search, X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  LEARN_COLUMNS,
  MegaMenu,
  type MegaMenuColumn,
  START_COLUMNS,
  TECHNIQUES_COLUMNS,
  TOOLS_COLUMNS,
} from '@/components/MegaMenu';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useAuth } from '@/hooks/useAuth';
import { useCommandPalette } from '@/hooks/useCommandPalette';
import { cn } from '@/lib/utils';
import { siteConfig } from '../../site.config';

export function Header() {
  return (
    <header
      className="sticky top-0 z-40 w-full bg-bg/95 supports-[backdrop-filter]:bg-bg/85 backdrop-blur-[6px]"
      style={{ boxShadow: '0 1px 0 hsl(var(--rule))' }}
    >
      <div className="container-prose flex h-16 items-center gap-4">
        <Link
          to="/"
          aria-label="AIEvals home"
          className="wordmark text-lg tracking-tight text-ink"
        >
          AI<span className="wordmark-accent">Evals</span>
        </Link>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-0.5">
          <MegaMenu label="Learn" href="/learn" columns={LEARN_COLUMNS} />
          <MegaMenu label="Start here" href="/start" columns={START_COLUMNS} />
          <MegaMenu label="Techniques" href="/techniques" columns={TECHNIQUES_COLUMNS} />
          <MegaMenu label="Tools" href="/tools" columns={TOOLS_COLUMNS} />
          <HeaderNavLink to="/task-types">Task types</HeaderNavLink>
          <HeaderNavLink to="/papers">Papers</HeaderNavLink>
          <HeaderNavLink to="/cookbook">Cookbook</HeaderNavLink>
        </nav>

        <div className="ml-auto flex items-center gap-1">
          <SearchTrigger />
          <AuthControl />
          <ThemeToggle />
          <MobileNav />
        </div>
      </div>
    </header>
  );
}

function HeaderNavLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex h-9 items-center rounded-md px-2.5 text-sm font-medium text-ink-soft transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      {children}
    </Link>
  );
}

/**
 * Renders nothing when auth is dark (no Cognito vars at build time).
 * Anonymous: a quiet sign-in button. Signed in: a small account link
 * greeting by first name when the profile has one, otherwise the email
 * local-part. Hidden below lg; the mobile sheet carries the same
 * affordance.
 */
function AuthControl() {
  const { status, restoring, email, givenName, signIn } = useAuth();
  if (status === 'disabled') return null;
  // Avoid a "Sign in" flash for returning readers while their stored
  // session restores.
  if (restoring) return null;

  if (status === 'anonymous') {
    return (
      <button
        type="button"
        onClick={() => signIn()}
        className="btn-ghost hidden h-9 items-center px-3 text-sm font-medium lg:inline-flex"
      >
        Sign in
      </button>
    );
  }

  const localPart = givenName || email?.split('@')[0] || 'Account';
  return (
    <Link
      to="/account"
      aria-label="Your account"
      className="btn-ghost hidden h-9 max-w-44 items-center gap-2 px-3 text-sm font-medium lg:inline-flex"
    >
      <CircleUserRound
        className="h-4 w-4 shrink-0"
        strokeWidth={1.75}
        aria-hidden="true"
      />
      <span className="truncate">{localPart}</span>
    </Link>
  );
}

function SearchTrigger() {
  const { setOpen } = useCommandPalette();
  return (
    <button
      type="button"
      aria-label="Search"
      onClick={() => setOpen(true)}
      className="btn-ghost inline-flex h-9 items-center gap-2 px-3 text-sm"
    >
      <Search className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
      <span className="hidden sm:inline">Search</span>
      <kbd className="hidden rounded border border-rule px-1.5 py-0.5 font-mono text-[10px] text-ink-mute sm:inline">
        {'⌘'}K
      </kbd>
    </button>
  );
}

function MobileNav() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      if (window.matchMedia('(min-width: 1024px)').matches) setOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label="Open navigation menu"
          className="btn-ghost inline-flex h-9 w-9 items-center justify-center p-0 lg:hidden"
        >
          <Menu className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
        </button>
      </DialogTrigger>
      <DialogContent
        className="left-auto right-0 top-0 grid h-dvh w-[88%] max-w-sm translate-x-0 translate-y-0 gap-0 rounded-none border-l border-rule p-0"
      >
        <div className="flex h-16 items-center justify-between border-b border-rule px-4">
          <DialogTitle className="wordmark text-lg text-ink">
            AI<span className="wordmark-accent">Evals</span>
          </DialogTitle>
          <DialogClose
            aria-label="Close navigation menu"
            className="btn-ghost inline-flex h-9 w-9 items-center justify-center p-0"
          >
            <X className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
          </DialogClose>
        </div>

        <nav aria-label="Mobile" className="flex flex-1 flex-col overflow-y-auto px-2 py-3">
          <MobileSection
            label="Learn"
            href="/learn"
            columns={LEARN_COLUMNS}
            onNavigate={() => setOpen(false)}
          />
          <MobileSection
            label="Start here"
            href="/start"
            columns={START_COLUMNS}
            onNavigate={() => setOpen(false)}
          />
          <MobileSection
            label="Techniques"
            href="/techniques"
            columns={TECHNIQUES_COLUMNS}
            onNavigate={() => setOpen(false)}
          />
          <MobileSection
            label="Tools"
            href="/tools"
            columns={TOOLS_COLUMNS}
            onNavigate={() => setOpen(false)}
          />
          <MobileLink to="/task-types" onNavigate={() => setOpen(false)}>
            Task types
          </MobileLink>
          <MobileLink to="/papers" onNavigate={() => setOpen(false)}>
            Papers
          </MobileLink>
          <MobileLink to="/cookbook" onNavigate={() => setOpen(false)}>
            Cookbook
          </MobileLink>

          <div className="my-3 border-t border-rule" aria-hidden="true" />

          <MobileLink to="/about" onNavigate={() => setOpen(false)}>
            About
          </MobileLink>
          <MobileLink to="/contact" onNavigate={() => setOpen(false)}>
            Contact
          </MobileLink>
          <MobileLink to="/contributing" onNavigate={() => setOpen(false)}>
            Contributing
          </MobileLink>
          <MobileAuthItem onNavigate={() => setOpen(false)} />

          <div className="mt-4 flex items-center justify-between px-3 py-2">
            <span className="text-xs uppercase tracking-wider text-ink-soft">Theme</span>
            <ThemeToggle />
          </div>

          {siteConfig.social.github ? (
            <a
              href={siteConfig.social.github}
              target="_blank"
              rel="noreferrer noopener"
              className="block rounded-md px-3 py-2 text-sm font-medium text-ink-soft transition-colors hover:bg-bg-mute hover:text-ink"
            >
              GitHub
            </a>
          ) : null}
        </nav>
      </DialogContent>
    </Dialog>
  );
}

/** Mobile-sheet counterpart of AuthControl; renders nothing when auth is dark. */
function MobileAuthItem({ onNavigate }: { onNavigate: () => void }) {
  const { status, restoring, email, givenName, signIn } = useAuth();
  if (status === 'disabled') return null;
  if (restoring) return null;

  if (status === 'anonymous') {
    return (
      <button
        type="button"
        onClick={() => {
          onNavigate();
          signIn();
        }}
        className="block w-full rounded-md px-3 py-2 text-left text-sm font-medium text-ink-soft transition-colors hover:bg-bg-mute hover:text-ink"
      >
        Sign in
      </button>
    );
  }

  const localPart = givenName || email?.split('@')[0] || 'Account';
  return (
    <MobileLink to="/account" onNavigate={onNavigate}>
      <span className="inline-flex items-center gap-2">
        <CircleUserRound
          className="h-4 w-4 shrink-0"
          strokeWidth={1.75}
          aria-hidden="true"
        />
        <span className="truncate">{localPart}</span>
      </span>
    </MobileLink>
  );
}

function MobileLink({
  to,
  onNavigate,
  children,
}: {
  to: string;
  onNavigate: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className="block rounded-md px-3 py-2 text-sm font-medium text-ink-soft transition-colors hover:bg-bg-mute hover:text-ink"
    >
      {children}
    </Link>
  );
}

function MobileSection({
  label,
  href,
  columns,
  onNavigate,
}: {
  label: string;
  href: string;
  columns: MegaMenuColumn[];
  onNavigate: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-rule/70 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-bg-mute"
      >
        {label}
        <ChevronDown
          className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
          strokeWidth={1.75}
          aria-hidden="true"
        />
      </button>
      {open ? (
        <div className="px-3 pb-2 pt-1">
          <Link
            to={href}
            onClick={onNavigate}
            className="mb-2 block rounded-md px-2 py-1.5 text-sm font-medium text-accent hover:bg-bg-mute"
          >
            Browse all {label}
          </Link>
          {columns.map((column, i) => (
            <div key={column.heading || `col-${i}`} className="mb-2 last:mb-0">
              {column.heading ? (
                <div className="px-1 pb-1 text-[10px] font-medium uppercase tracking-wider text-ink-soft">
                  {column.heading}
                </div>
              ) : null}
              <ul className="flex flex-col">
                {column.items.map((item) => (
                  <li key={item.href}>
                    {item.external ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noreferrer noopener"
                        onClick={onNavigate}
                        className="block rounded-md px-2 py-1.5 text-sm text-ink-soft hover:bg-bg-mute hover:text-ink"
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={onNavigate}
                        className="block rounded-md px-2 py-1.5 text-sm text-ink-soft hover:bg-bg-mute hover:text-ink"
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default Header;
